/**
 * Sales over Time section registry (frontend side).
 *
 * The server resolves and validates each client's dashboard config and sends
 * it as `dashboard` with the calendar list and the metrics payload. This file
 * only knows the fixed section order and the default (today's dashboard), and
 * turns whatever the server sent into something safe to render: unknown ids
 * are ignored, a missing or unusable block means the default.
 */
export const SECTION_KPI = "kpi_summary";
export const SECTION_TREND = "trend_chart";
export const SECTION_HISTORY = "history_tables";
export const SECTION_DAILY = "daily_detail";
export const SECTION_NOTES = "data_notes";

// Render order is fixed; a config only decides which sections appear.
export const SECTION_ORDER = [
  SECTION_KPI,
  SECTION_TREND,
  SECTION_HISTORY,
  SECTION_DAILY,
  SECTION_NOTES,
];

export const DEFAULT_KPI_METRICS = [
  "gross_sales",
  "orders",
  "units_sold",
  "upt",
  "gross_aov",
  "inventory_on_hand_current",
];

export const DEFAULT_DASHBOARD = Object.freeze({
  source: "default",
  sections: SECTION_ORDER,
  kpi_metrics: DEFAULT_KPI_METRICS,
});

export const normalizeDashboard = (block) => {
  if (!block || !Array.isArray(block.sections)) return DEFAULT_DASHBOARD;
  const listed = new Set(
    block.sections.filter((id) => SECTION_ORDER.includes(id)),
  );
  if (!listed.size || (listed.size === 1 && listed.has(SECTION_NOTES))) {
    return DEFAULT_DASHBOARD;
  }
  listed.add(SECTION_NOTES);
  const sections = SECTION_ORDER.filter((id) => listed.has(id));
  const kpiMetrics = Array.isArray(block.kpi_metrics)
    ? [...new Set(block.kpi_metrics.filter((id) => typeof id === "string"))]
    : DEFAULT_KPI_METRICS;
  return {
    source: block.source || "client",
    sections,
    kpi_metrics: sections.includes(SECTION_KPI) ? kpiMetrics : [],
  };
};

/**
 * KPI ids that can actually be shown: listed by the config AND present in the
 * payload as a real metric. An "unavailable" metric is never shown as a tile.
 */
export const visibleKpiIds = (dashboard, metrics) =>
  dashboard.kpi_metrics.filter(
    (id) => metrics?.[id] && metrics[id].status !== "unavailable",
  );

export const KPI_MIN_TILE_WIDTH = 190;
export const KPI_GAP_PX = 12;
export const KPI_NARROW_WIDTH = 600;
export const KPI_MAX_COLUMNS = 6;

/**
 * Balanced KPI rows: never leave a single tile alone on the last row when the
 * row can be split evenly (6 → 3+3 rather than 5+1, 8 → 4+4 rather than 7+1).
 * `width` 0 (not measured yet) lays every tile out on one row, capped at 6.
 */
export const kpiGridColumns = (count, width) => {
  if (count <= 0) return 1;
  let maxColumns;
  if (!width) {
    maxColumns = KPI_MAX_COLUMNS;
  } else if (width < KPI_NARROW_WIDTH) {
    maxColumns = 2;
  } else {
    maxColumns = Math.floor(
      (width + KPI_GAP_PX) / (KPI_MIN_TILE_WIDTH + KPI_GAP_PX),
    );
    maxColumns = Math.max(2, Math.min(KPI_MAX_COLUMNS, maxColumns));
  }
  const rows = Math.ceil(count / maxColumns);
  return Math.ceil(count / rows);
};

/** A single tile left on the last row spans it instead of sitting alone. */
export const kpiSpanLast = (count, columns) =>
  count > columns && count % columns === 1;
